import actions from './actions';
import { ConsortiaManagementTypes } from './types';
import {
  fetchConsortia as fetchConsortiaService,
  fetchConsortium as fetchConsortiumService,
  createConsortium as createConsortiumService,
  upsertConsortium as upsertConsortiumService,
  validateConsortium as validateConsortiumService,
  deleteConsortium as deleteConsortiumService,
} from '../../service/consortiaManagement';
import { displayNotification } from '../notification/actions';
import { NotificationActionTypes } from '../notification/types';
import { RootState } from '..';
import { Consortium } from '@cambridgeassessment/lib-ee-commons';

type Dispatch = (
  action:
    | ConsortiaManagementTypes
    | NotificationActionTypes
    | ((dispatch: Dispatch, getState: () => RootState) => Promise<void>),
) => void;

export const fetchConsortia =
  (refreshSearch?: boolean, setFetchState = true, loadedSuccessfullyResolver?: () => void) =>
  async (dispatch: Dispatch, getState: () => RootState): Promise<void> => {
    try {
      const state = getState();
      const { businessUnit } = state.userData;
      const {
        consortia,
        nextPageUrl,
        searchValue,
        fetchingConsortia,
        filter: { series },
      } = state.consortiaManagement;

      if (fetchingConsortia) {
        return;
      }

      if (consortia && !nextPageUrl && !refreshSearch) {
        dispatch(actions.setFetchingConsortia(false));
        return;
      }

      if (!businessUnit?.id) {
        return;
      }
      if (setFetchState) {
        dispatch(actions.setFetchingConsortia(true));
      }
      const { results, nextUrl } = await fetchConsortiaService(
        businessUnit?.id,
        series,
        searchValue,
        refreshSearch ? undefined : nextPageUrl,
      );
      const consortiumToAppend = refreshSearch ? [] : consortia;
      const appendedConsortium = [...(consortiumToAppend || []), ...results];
      const count = nextUrl ? appendedConsortium.length + 1 : appendedConsortium.length;

      dispatch(actions.setConsortiaTotal(count));
      dispatch(actions.setNextPageUrl(nextUrl || undefined));
      dispatch(actions.setFetchingConsortia(false));
      dispatch(actions.setConsortia(appendedConsortium));

      if (loadedSuccessfullyResolver) {
        loadedSuccessfullyResolver();
      }
    } catch (err) {
      dispatch(actions.setFetchingConsortia(false));
      dispatch(displayNotification('Failed to fetch consortia'));
    }
  };

export const fetchConsortium =
  (id: string) =>
  async (dispatch: Dispatch): Promise<void> => {
    try {
      dispatch(actions.setIsLoading(true));
      const result = await fetchConsortiumService(id);
      dispatch(actions.setConsortium(result));
    } catch (err) {
      dispatch(displayNotification('Failed to fetch consortium'));
    } finally {
      dispatch(actions.setIsLoading(false));
    }
  };

export const submitConsortium =
  (consortium: Consortium) =>
  async (dispatch: Dispatch, getState: () => RootState): Promise<boolean | undefined> => {
    try {
      const businessUnitId = getState().userData.businessUnit?.id;
      if (!businessUnitId) {
        return;
      }
      const result = await validateConsortiumService(businessUnitId, consortium);
      dispatch(actions.setValididation(result));
      const { isConsortiumUnique, isNameUnique } = result;
      if (!isConsortiumUnique) {
        dispatch(
          displayNotification(
            'The same centre is in more than one consortium, for the same component, and for the same series.',
          ),
        );
      }
      if (isConsortiumUnique && isNameUnique) {
        consortium._id ? dispatch(upsertConsortium(consortium)) : dispatch(createConsortium(consortium));
      }
    } catch (err) {
      dispatch(displayNotification('Failed to submit consortium'));
    }
  };

export const upsertConsortium =
  (consortium: Consortium) =>
  async (dispatch: Dispatch): Promise<void> => {
    try {
      if (!consortium._id) {
        return;
      }
      await upsertConsortiumService(consortium._id, consortium);
      dispatch(displayNotification('The consortium was edited successfully.'));
      dispatch(actions.setSubmittedSuccessfully(true));
    } catch (err) {
      dispatch(displayNotification('Failed to update consortium'));
    }
  };

export const createConsortium =
  (consortium: Consortium) =>
  async (dispatch: Dispatch, getState: () => RootState): Promise<void> => {
    try {
      const { businessUnit } = getState().userData;
      const businessUnitId = businessUnit?.id;
      if (!businessUnitId) {
        return;
      }
      await createConsortiumService(businessUnitId, consortium);
      dispatch(displayNotification('The consortium was created successfully. '));
      dispatch(actions.setSubmittedSuccessfully(true));
    } catch (err) {
      dispatch(displayNotification('Failed to create consortium'));
    }
  };

export const deleteConsortium =
  (consortiumId: string) =>
  async (dispatch: Dispatch, getState: () => RootState): Promise<void> => {
    try {
      const state = getState();
      const { consortia } = state.consortiaManagement;
      const { businessUnit } = state.userData;

      if (!businessUnit?.id) {
        return;
      }

      await deleteConsortiumService(consortiumId);

      dispatch(displayNotification('The consortium was deleted successfully.'));

      if (!consortia) {
        return;
      }

      dispatch(actions.setConsortia([]));

      const consortiumIndex = consortia.findIndex((consortia) => consortia._id === consortiumId);
      const newConsortia = [...consortia];
      newConsortia.splice(consortiumIndex, 1);

      dispatch(actions.setConsortia(newConsortia));
    } catch (e) {
      dispatch(displayNotification('Failed to delete consortium.'));
    }
  };

export default {
  fetchConsortia,
  fetchConsortium,
  createConsortium,
  submitConsortium,
  upsertConsortium,
  deleteConsortium,
};
